import React, { useEffect, useState } from 'react';
import Icon from './shared/Icon';
import CascaDeModal from './shared/CascaDeModal';
import { invoicesService, fiscalService } from '../services/api';
import { fmtDateTime } from '../utils/formatters';
import {
  AnexoDoComprovante,
  Comprovante,
  MOTIVO_SEM_COMPROVANTE,
  RespostaDosComprovantes,
  fmtTamanho,
  lerAnexo,
  ordenarComprovantes,
  rotuloDoCaminho,
} from '../utils/comprovanteDeEnvio';

/**
 * COMPROVANTE DE ENVIO: abre do selo de e-mail da lista de faturas.
 * Três abas: "O que foi enviado" (o comprovante, novo), "Trilha" (o popup que já
 * existia, intacto, com o reenviar por destinatário) e "Destinatários".
 * Regras em utils/comprovanteDeEnvio.ts.
 */
export interface DestinatarioDaTrilha {
  email: string;
  status: string;
  cor: string;
  ultimoEnvio?: string | null;
  tentativas: number;
}

interface Props {
  invoiceId: number;
  invoiceCode: string;
  clienteNome: string;
  rotuloDoModelo: (tipo: string) => string;
  /** O conteúdo do popup antigo, por destinatário, com o reenviar. */
  trilha: React.ReactNode;
  destinatarios: DestinatarioDaTrilha[];
  onClose: () => void;
}

const abrirFiscal = async (a: AnexoDoComprovante) => {
  if (a.origem.tipo !== 'documento_fiscal') return;
  const id = a.origem.documentoId;
  if (/xml/i.test(a.tipo) || /\.xml$/i.test(a.nome)) {
    await fiscalService.downloadXml(id, a.nome);
    return;
  }
  const doc = await fiscalService.getById(id);
  const data = String(doc?.type || '').toLowerCase() === 'nfcom' ? await fiscalService.downloadNfcomPdf(id) : await fiscalService.downloadNfsePdf(id);
  if (data?.url) window.open(data.url, '_blank');
};

function Anexo({ a }: { a: AnexoDoComprovante }) {
  const l = lerAnexo(a);
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ext = (a.nome.split('.').pop() || a.tipo.split('/').pop() || '').slice(0, 4).toUpperCase();
  return (
    <div className={`flex items-start gap-3 px-3 py-2 rounded-lg border ${l.divergente ? 'border-amber-500/40 bg-amber-500/5' : 'border-slate-700 bg-slate-900/40'}`}>
      <span className='shrink-0 w-9 h-9 rounded-md bg-slate-800 border border-slate-700 flex items-center justify-center font-mono text-[10px] text-slate-400'>{ext || '—'}</span>
      <div className='flex-1 min-w-0'>
        {l.acao === 'fiscal' ? (
          <button
            type='button'
            disabled={abrindo}
            onClick={async () => {
              setAbrindo(true);
              setErro(null);
              try { await abrirFiscal(a); } catch (e: any) { setErro(e?.response?.data?.message || 'Não foi possível abrir o documento'); }
              setAbrindo(false);
            }}
            className='text-sm text-sky-300 hover:text-sky-200 hover:underline truncate max-w-full text-left disabled:opacity-60'
            title='Abre o documento como está hoje'
          >
            {abrindo ? 'Abrindo…' : a.nome}
          </button>
        ) : (
          <div className='text-sm text-slate-200 truncate' title={a.nome}>{a.nome}</div>
        )}
        <div className={`text-[11px] ${l.tom === 'aviso' ? 'text-amber-300' : l.tom === 'ok' ? 'text-slate-400' : 'text-slate-500'}`}>
          {l.divergente && '⚠ '}{l.estado}
        </div>
        {erro && <div className='text-[11px] text-red-300'>{erro}</div>}
      </div>
      <span className='shrink-0 font-mono text-xs text-slate-400 pt-0.5' title='Tamanho do arquivo como foi enviado'>{fmtTamanho(a.tamanho)}</span>
    </div>
  );
}

function SemComprovante() {
  return (
    <div className='rounded-lg border border-slate-600 bg-slate-900/60 px-4 py-5 text-center'>
      <Icon name='fileText' className='w-8 h-8 mx-auto mb-2 text-slate-500' />
      <p className='text-sm font-medium text-slate-200'>Envio anterior ao comprovante, conteúdo não registrado</p>
      <p className='text-xs text-slate-400 mt-1.5 max-w-md mx-auto'>
        Até 02/10/2026 o HUB guardava para quem o e-mail foi e se chegou, mas não o corpo nem os anexos. Este envio é desse período: a
        trilha e os destinatários existem; o conteúdo não. Ele não é reconstruído a partir do modelo atual, porque o modelo muda e o
        comprovante passaria a afirmar algo que talvez não tenha sido enviado.
      </p>
    </div>
  );
}

export default function ComprovanteDeEnvioModal({ invoiceId, invoiceCode, clienteNome, rotuloDoModelo, trilha, destinatarios, onClose }: Props) {
  const [aba, setAba] = useState<'enviado' | 'trilha' | 'destinatarios'>('enviado');
  const [resp, setResp] = useState<RespostaDosComprovantes | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [selecionado, setSelecionado] = useState<number | null>(null);

  useEffect(() => {
    let vivo = true;
    invoicesService
      .getComprovantesDeEnvio(invoiceId)
      .then((r: RespostaDosComprovantes) => { if (vivo) setResp(r); })
      .catch((e: any) => { if (vivo) setErro(e?.response?.data?.message || e?.message || 'Não foi possível ler o comprovante.'); });
    return () => { vivo = false; };
  }, [invoiceId]);

  const lista = ordenarComprovantes(resp?.comprovantes || []);
  const atual: Comprovante | undefined = lista.find((c) => c.id === selecionado) || lista[0];
  const anexos = atual?.anexos || [];
  const totalAnexos = anexos.reduce((s, a) => s + (Number(a.tamanho) || 0), 0);

  return (
    // Mesma moldura das fichas (CascaDeModal). Acima do selo da lista: z alto.
    <CascaDeModal
      largura='md'
      // Altura FIXA (pedido do Heitor de 04/11/2026): as três abas têm tamanhos muito diferentes, e a caixa crescia e
      // encolhia a cada troca. A casca rola o conteúdo por dentro, como no Editar Fatura.
      altura='fixa'
      zIndex='z-[9998]'
      onClose={onClose}
      titulo={clienteNome}
      subtitulo={`${invoiceCode} · comprovante de envio${atual?.enviadoEm ? ` · ${fmtDateTime(atual.enviadoEm)}` : ''}`}
      abas={[
        { chave: 'enviado', rotulo: 'O que foi enviado' },
        { chave: 'trilha', rotulo: 'Trilha' },
        { chave: 'destinatarios', rotulo: `Destinatários (${destinatarios.length})` },
      ]}
      abaAtiva={aba}
      onAba={(k) => setAba(k as 'enviado' | 'trilha' | 'destinatarios')}
      corpoClassName='px-5 py-4'
      rodape={resp && !atual && aba !== 'enviado' ? <span className='text-xs text-slate-500'>{MOTIVO_SEM_COMPROVANTE}</span> : undefined}
    >
          {aba === 'enviado' && (
            erro ? <p className='text-sm text-red-300'>{erro}</p>
            : !resp ? <p className='text-sm text-slate-500'>Lendo o comprovante…</p>
            : !atual ? <SemComprovante />
            : (
              <div className='space-y-4'>
                {lista.length > 1 && (
                  <div className='flex flex-wrap items-center gap-1.5 text-xs'>
                    <span className='text-slate-500 mr-1'>Disparo:</span>
                    {lista.map((c, i) => (
                      <button
                        key={c.id}
                        type='button'
                        onClick={() => setSelecionado(c.id ?? null)}
                        className={`px-2 py-0.5 rounded-full border ${c.id === atual.id ? 'bg-orange-500/15 border-orange-500/50 text-orange-200' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}
                      >
                        {i === 0 ? 'último · ' : ''}{fmtDateTime(c.enviadoEm)} · {rotuloDoCaminho(c.caminho)}
                      </button>
                    ))}
                  </div>
                )}

                <div className='divide-y divide-slate-800 text-sm'>
                  {[
                    ['assunto', atual.assunto || '—'],
                    ['para', (atual.destinatarios || []).join(', ') || '—'],
                    ['modelo', atual.modeloDeEntrega ? `${rotuloDoModelo(atual.modeloDeEntrega)} (${atual.modeloDeEntrega})` : '—'],
                    ['por', `${rotuloDoCaminho(atual.caminho)}${atual.enviadoPor ? ` · ${atual.enviadoPor}` : ''}`],
                  ].map(([k, v]) => (
                    <div key={k} className='flex gap-3 py-1.5'>
                      <span className='w-20 shrink-0 font-mono text-xs text-slate-500 pt-0.5'>{k}</span>
                      <span className='flex-1 min-w-0 text-slate-200 break-words'>{v}</span>
                    </div>
                  ))}
                </div>

                {/* A nota avulsa do disparo: antes sumia sem deixar rastro. */}
                {atual.notaDoOperador ? (
                  <div className='rounded-lg border-l-2 border-orange-400 bg-orange-500/10 px-3 py-2'>
                    <div className='text-[10px] uppercase tracking-wider text-orange-300 mb-0.5'>Nota incluída no envio</div>
                    <p className='text-sm text-slate-200 whitespace-pre-line'>{atual.notaDoOperador}</p>
                  </div>
                ) : (
                  <p className='text-xs text-slate-500'>Nenhuma nota avulsa neste envio.</p>
                )}

                <div>
                  <div className='text-[10px] uppercase tracking-wider text-slate-500 mb-1.5'>
                    Anexos · {anexos.length} {anexos.length === 1 ? 'arquivo' : 'arquivos'}{anexos.length ? ` · ${fmtTamanho(totalAnexos)}` : ''}
                  </div>
                  {anexos.length === 0 ? (
                    <p className='text-sm text-slate-400'>Este envio não levou anexo.</p>
                  ) : (
                    <div className='space-y-1.5'>{anexos.map((a, i) => <Anexo key={i} a={a} />)}</div>
                  )}
                </div>

                <div>
                  <div className='text-[10px] uppercase tracking-wider text-slate-500 mb-1.5'>Corpo, como o cliente viu</div>
                  {atual.corpoHtml ? (
                    // sandbox sem permissões: o HTML do e-mail é exibido, nunca executado.
                    <iframe title='Corpo do e-mail' sandbox='' srcDoc={atual.corpoHtml} className='w-full h-[420px] rounded-lg border border-central-borda bg-white' />
                  ) : (
                    <p className='text-sm text-slate-400'>O corpo deste disparo veio vazio.</p>
                  )}
                </div>
              </div>
            )
          )}

          {aba === 'trilha' && trilha}

          {aba === 'destinatarios' && (
            <div className='divide-y divide-slate-800 text-sm'>
              {destinatarios.length === 0 && <p className='text-slate-500'>Nenhum destinatário registrado.</p>}
              {destinatarios.map((d) => (
                <div key={d.email} className='flex items-center gap-3 py-2'>
                  <span className='flex-1 min-w-0 truncate text-slate-200' title={d.email}>{d.email}</span>
                  {d.ultimoEnvio && <span className='text-xs text-slate-500 font-mono'>{fmtDateTime(d.ultimoEnvio)}</span>}
                  {d.tentativas > 1 && <span className='text-[11px] text-slate-500'>{d.tentativas} envios</span>}
                  <span className={`shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${d.cor}`} data-selo-do-envio>{d.status}</span>
                </div>
              ))}
            </div>
          )}
    </CascaDeModal>
  );
}
